import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Clock, Eye, EyeOff, Fingerprint, WifiOff, Wifi, CloudOff } from 'lucide-react';
import { toast } from 'sonner';

const SAVED_CREDENTIALS_KEY = 'jornyx_saved_credentials';
const EMPRESA_PARAM_KEY = 'jornyx_empresa_id';

const UnifiedLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isOnline } = useUnifiedAuth();
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [empresaInfo, setEmpresaInfo] = useState(null);

  // Verificar parâmetro de empresa na URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const empresaId = urlParams.get('empresa');
    
    if (empresaId) {
      // Salvar o ID da empresa para usar após login
      localStorage.setItem(EMPRESA_PARAM_KEY, empresaId);
      
      // Buscar info da empresa para mostrar
      fetchEmpresaInfo(empresaId);
      
      // Limpar parâmetro da URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchEmpresaInfo = async (empresaId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/empresas/${empresaId}/info-publica`);
      if (res.ok) {
        const data = await res.json();
        setEmpresaInfo(data);
      }
    } catch (err) {
      console.error('Erro ao buscar info da empresa:', err);
    }
  };

  // Carregar credenciais salvas ao iniciar
  useEffect(() => {
    const saved = localStorage.getItem(SAVED_CREDENTIALS_KEY);
    if (saved) {
      try {
        const { cpf: savedCpf, password: savedPwd } = JSON.parse(saved);
        setCpf(savedCpf || '');
        setPassword(savedPwd || '');
        setRememberMe(true);
      } catch (e) {
        localStorage.removeItem(SAVED_CREDENTIALS_KEY);
      }
    }
  }, []);

  // Se já está autenticado, redirecionar para home
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/unified');
    }
  }, [isAuthenticated, navigate]);

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
  };

  const handleCPFChange = (e) => {
    setCpf(formatCPF(e.target.value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cpf || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      const result = await login(cpf, password);
      
      if (result.success) {
        // Salvar credenciais se "Lembrar-me" estiver marcado
        if (rememberMe) {
          localStorage.setItem(SAVED_CREDENTIALS_KEY, JSON.stringify({ cpf, password }));
        } else {
          localStorage.removeItem(SAVED_CREDENTIALS_KEY);
        }
        
        if (result.offline) {
          toast.success('Login offline realizado! Dados serão sincronizados quando conectar.');
        } else {
          toast.success('Login realizado com sucesso!');
        }
        navigate('/unified');
      }
    } catch (error) {
      toast.error(error.message || 'CPF ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900 flex items-center justify-center p-4">
      {/* Indicador de status offline */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-amber-950 py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2 z-50">
          <WifiOff className="w-4 h-4" />
          Modo Offline - Você pode fazer login se já tiver acessado antes
        </div>
      )}
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo e título */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30 mb-4">
            <Clock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">JORNYX</h1>
          <p className="text-slate-400">Gestão de Jornada de Trabalho</p>
        </div>

        {/* Empresa identificada pelo link */}
        {empresaInfo && (
          <Card className="mb-4 border-emerald-500/50 bg-emerald-500/10 backdrop-blur">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Fingerprint className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-emerald-300">Empresa identificada</p>
                  <p className="text-sm font-semibold text-white">{empresaInfo.nome}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Formulário */}
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cpf" className="text-slate-700">CPF</Label>
                <Input
                  id="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={handleCPFChange}
                  className="h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                  disabled={loading}
                  autoComplete="username"
                  data-testid="input-cpf"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 pr-10"
                    disabled={loading}
                    autoComplete="current-password"
                    data-testid="input-senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Senha padrão: últimos 6 dígitos do CPF</p>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox 
                  id="remember" 
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                />
                <Label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                  Lembrar meus dados
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium"
                disabled={loading}
                data-testid="btn-entrar"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <Fingerprint className="mr-2 h-4 w-4" />
                    Entrar
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Problemas para acessar? Contate o RH da sua empresa.
        </p>
      </div>
    </div>
  );
};

export default UnifiedLogin;
